import { buildPanelButton } from './requestPanelButton'
import {
    flushUploadReport,
    recordUploadEvent,
    startUploadAttempt,
    uploadRequestStats
} from './requestUploadDiagnostics'

// Formie's upload manager handles drag & drop, uploading and removal itself, but it runs Uppy
// headless: a file refused on size or type is announced through `uppy.info()`, which has no UI
// attached, and an upload that never starts leaves its item on "Preparing…" forever. Both look
// the same to a visitor — nothing happens, or a bar that stops moving — so this module supplies
// the missing half: it says what went wrong, offers a way out, and feeds the same observations to
// `requestUploadDiagnostics.js`.
//
// It also shows the state in Dutch. Formie writes "Preparing…" / "Uploading… 42%" / "Processing…"
// into its own label, which our CSS hides because it is English; a separate element next to it
// keeps that information without fighting Formie over the text.

const rootSelector = '[data-formie-upload-manager-root]'
const dropzoneSelector = '[data-formie-upload-manager]'
const inputSelector = '[data-formie-upload-manager-input]'
const listSelector = '[data-formie-upload-manager-list]'
const itemSelector = '[data-formie-upload-manager-item]'
const progressSelector = '[data-formie-upload-manager-progress]'
const progressBarSelector = '[data-formie-upload-manager-progress-bar]'
const filenameSelector = '[data-formie-upload-manager-filename]'
const labelSelector = '[data-formie-upload-manager-progress-label]'
const removeSelector = '[data-formie-upload-manager-remove]'

// Formie writes its phase twice: as `is-<status>` on the item and as English text in the progress
// label. Neither alone is enough — the classes can overlap (an item carrying both `is-preparing`
// and `is-uploading` is normal) while the label is the last thing Formie's own code decided, and
// the label is not touched at all when an upload fails.
const statuses = [
    'error',
    'complete',
    'upload-complete',
    'processing',
    'retrying',
    'preparing',
    'uploading'
]

const labelStatuses = [
    ['preparing', /^preparing/i],
    ['uploading', /^uploading/i],
    ['processing', /^processing/i],
    ['retrying', /^retry/i],
    ['complete', /^complete/i]
]

const terminalStatuses = ['complete', 'upload-complete', 'error']

const statusLabels = {
    preparing: 'Voorbereiden…',
    uploading: 'Opladen',
    processing: 'Verwerken…',
    retrying: 'Opnieuw proberen…',
    'upload-complete': 'Klaar',
    complete: 'Klaar',
    error: 'Mislukt'
}

const messages = {
    size: (file, limit) =>
        `${file.name} is te groot (${formatSize(file.size)}). Kies een foto van maximaal ${limit} MB, of verklein ze eerst.`,
    type: 'Dit bestand kunnen we niet verwerken. Kies een foto — bijvoorbeeld een JPG of een PNG.',
    stalled:
        'Het opladen kwam niet op gang. Probeer het opnieuw — lukt het dan nog steeds niet, kies dan een andere foto of probeer het op een ander toestel.',
    slow: 'Het opladen duurt langer dan gewoonlijk. Even geduld en laat deze pagina open staan.'
}

const retryLabel = 'Opnieuw proberen'

// How long each phase may last before it counts as stuck. "Preparing" is short because nothing
// legitimately happens there: Uppy starts the request within milliseconds of adding the file.
const noRequestAfter = 8000
const noProgressAfter = 25000
const stalledAfter = 90000
const processingAfter = 45000
const watchdogInterval = 1000
const watchdogTimeout = 240000

const isSamsungInternet = () =>
    /SamsungBrowser/i.test(navigator.userAgent || '')

const formatSize = (bytes) => `${Math.round((bytes / 1000000) * 10) / 10} MB`

const readLabel = (item) =>
    item.querySelector(labelSelector)?.textContent?.trim() ?? ''

const readStatus = (item) => {
    if (item.classList.contains('is-error')) {
        return 'error'
    }

    const label = readLabel(item)
    const fromLabel = labelStatuses.find(([, pattern]) =>
        pattern.test(label)
    )?.[0]

    return (
        fromLabel ??
        statuses.find((status) => item.classList.contains(`is-${status}`)) ??
        'unknown'
    )
}

// `data-progress` holds a percentage only while bytes are actually moving; in every other phase
// Formie parks the status name in it ("preparing", "processing", "retrying").
const readProgress = (item) => {
    const value = Number(
        item.querySelector(progressBarSelector)?.getAttribute('data-progress')
    )

    return Number.isFinite(value) ? value : 0
}

// The stored name first: what is on screen is deliberately shortened, and a diagnostics log wants
// the file the visitor actually picked.
const readFilename = (item) => {
    const element = item.querySelector(filenameSelector)

    return (
        element?.dataset.requestUploadFilename ||
        element?.textContent?.trim() ||
        'bestand'
    )
}

const describeItem = (item) =>
    `${readFilename(item)} status=${readStatus(item)} progress=${readProgress(item)}% label="${readLabel(item)}" classes="${item.className}"`

const getItems = (root) => Array.from(root.querySelectorAll(itemSelector))

/**
 * Uppy's own restriction check, run before Uppy gets to run it silently. `maxFileSize` is the
 * megabyte limit from the field settings, the same multiplication Formie does (`limit * 1000 *
 * 1000`), and the accept list is matched the way Uppy matches it: a leading dot is an extension,
 * a trailing `/*` a mime group, anything else an exact mime type.
 */
const refusalReason = (file, root) => {
    const dropzone = root.querySelector(dropzoneSelector)
    const input = root.querySelector(inputSelector)
    const limit = parseFloat(
        dropzone?.getAttribute('data-formie-size-max-limit') || ''
    )

    if (limit && file.size > limit * 1000 * 1000) {
        return { reason: 'size', limit }
    }

    const accept = (input?.getAttribute('accept') || '')
        .split(',')
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)

    if (!accept.length) {
        return null
    }

    const name = (file.name || '').toLowerCase()
    const type = (file.type || '').toLowerCase()

    const allowed = accept.some((entry) => {
        if (entry.startsWith('.')) {
            return name.endsWith(entry)
        }

        if (entry.endsWith('/*')) {
            return type.startsWith(entry.slice(0, -1))
        }

        return type === entry
    })

    return allowed ? null : { reason: 'type' }
}

const getMessagePanel = (root) => {
    let panel = root.querySelector('[data-request-upload-message]')

    if (panel) {
        return panel
    }

    panel = document.createElement('div')
    panel.className = 'request-form__upload-message'
    panel.dataset.requestUploadMessage = 'true'
    panel.setAttribute('role', 'alert')
    panel.hidden = true

    const text = document.createElement('p')

    text.className = 'request-form__upload-message-text'
    text.dataset.requestUploadMessageText = 'true'
    panel.appendChild(text)

    root.appendChild(panel)

    return panel
}

const hideMessage = (root) => {
    const panel = root.querySelector('[data-request-upload-message]')

    if (panel) {
        panel.hidden = true
        panel.querySelector('[data-request-upload-retry]')?.remove()
    }
}

/**
 * `tone` is the difference between "this went wrong" and "this is taking a while": only the first
 * gets the site's error language, the same one a failed analysis and a rejected field use.
 */
const showMessage = (root, text, { tone = 'error', onRetry = null } = {}) => {
    const panel = getMessagePanel(root)

    panel.classList.toggle(
        'request-form__upload-message--error',
        tone === 'error'
    )
    panel.querySelector('[data-request-upload-message-text]').textContent = text
    panel.querySelector('[data-request-upload-retry]')?.remove()
    panel.hidden = false

    if (!onRetry) {
        return
    }

    // The same button the failed-analysis panel offers, in the same error colours as this panel.
    const button = buildPanelButton(retryLabel, 'error')

    button.dataset.requestUploadRetry = 'true'
    button.addEventListener('click', () => {
        recordUploadEvent('ui:retry')
        onRetry()
    })

    panel.appendChild(button)
}

/**
 * Clears the stuck item through Formie's own remove button — which also tells Uppy to forget the
 * file, the one thing we cannot do from here — and reopens the picker.
 */
const retryUpload = (root) => {
    // The attempt is over as far as the watchdog is concerned; choosing a file starts a new one.
    stopWatchdog(root)

    getItems(root)
        .filter((item) => !terminalStatuses.includes(readStatus(item)))
        .forEach((item) => item.querySelector(removeSelector)?.click())

    hideMessage(root)
    root.querySelector(inputSelector)?.click()
}

/**
 * Formie writes the file name into a single span, which leaves two problems.
 *
 * The browser cuts a too-long name off at the end, so the extension — the one part that says what
 * the file actually is — is the first thing to go. And an ellipsis is only paint: the full text
 * still counts towards the width the layout asks for, so a long name kept pushing the field past
 * the edge of its container no matter how it was displayed.
 *
 * So the name is cut in the DOM rather than only in the CSS, short on a phone and roomier from
 * `sm` up, with the extension kept on the end. The CSS ellipsis on the first half stays as a
 * second net for the narrowest screens. The full name lives on in the tooltip.
 */
const filenameBreakpoint = window.matchMedia('(max-width: 639px)')

const filenameLimits = { narrow: 18, wide: 40 }

const renderFilename = (element) => {
    const name = element.dataset.requestUploadFilename

    if (!name) {
        return
    }

    // Everything from the last dot on, as long as there is a name in front of it: a leading dot is
    // part of the name, not an extension, and a dotless name has none at all.
    const cut = name.lastIndexOf('.')
    const hasExtension = cut > 0 && cut < name.length - 1
    const extension = hasExtension ? name.slice(cut) : ''
    const stem = hasExtension ? name.slice(0, cut) : name

    const limit = filenameBreakpoint.matches
        ? filenameLimits.narrow
        : filenameLimits.wide

    const shortened =
        stem.length > limit ? `${stem.slice(0, limit).trimEnd()}…` : stem

    const stemElement = document.createElement('span')

    stemElement.className = 'request-form__upload-filename-stem'
    stemElement.textContent = shortened

    element.textContent = ''
    element.appendChild(stemElement)

    if (!hasExtension) {
        return
    }

    const extensionElement = document.createElement('span')

    extensionElement.className = 'request-form__upload-filename-ext'
    extensionElement.textContent = extension
    element.appendChild(extensionElement)
}

/**
 * Runs once per item: the stored name is both the source for `renderFilename` and the flag that
 * keeps the observer calling us from reacting to our own rewrite.
 */
const prepareFilename = (item) => {
    const element = item.querySelector(filenameSelector)

    if (!element || element.dataset.requestUploadFilename) {
        return
    }

    const name = element.textContent.trim()

    if (!name) {
        return
    }

    element.dataset.requestUploadFilename = name
    element.title = name

    renderFilename(element)
}

// Rotating a phone or dragging a window across the breakpoint should change how much of the name
// fits, the same way a media query would.
filenameBreakpoint.addEventListener('change', () => {
    document
        .querySelectorAll(`${filenameSelector}[data-request-upload-filename]`)
        .forEach(renderFilename)
})

const setStatusText = (item) => {
    const container = item.querySelector(progressSelector)

    if (!container) {
        return
    }

    let element = container.querySelector('[data-request-upload-status]')

    if (!element) {
        element = document.createElement('span')
        element.className = 'request-form__upload-status'
        element.dataset.requestUploadStatus = 'true'
        container.appendChild(element)
    }

    const status = readStatus(item)
    const label = statusLabels[status] ?? ''
    const progress = readProgress(item)
    const text =
        status === 'uploading' && progress > 0
            ? `${label}… ${progress}%`
            : label

    // Writing unconditionally would feed the observer that called us its own mutation, over and
    // over.
    if (element.textContent !== text) {
        element.textContent = text
    }
}

const stopWatchdog = (root) => {
    window.clearInterval(root.uploadWatchdog)
    root.uploadWatchdog = null
}

// One timer per attempt, rather than one per phase: every rule is a question about how long the
// current state has been the current state.
const startWatchdog = (root) => {
    stopWatchdog(root)

    const startedAt = Date.now()
    const reported = new Set()
    let processingSince = null
    let sawItem = false
    // Files uploaded earlier in this session are still in the list, so "did anything appear?" is a
    // question about the count at the start of this attempt.
    const itemsAtStart = getItems(root).length

    const report = (name, detail, text = null, options = {}) => {
        if (reported.has(name)) {
            return
        }

        reported.add(name)
        recordUploadEvent(`watchdog:${name}`, detail)
        flushUploadReport(`watchdog:${name}`)

        if (text) {
            showMessage(root, text, options)
        }
    }

    root.uploadWatchdog = window.setInterval(() => {
        const elapsed = Date.now() - startedAt
        const items = getItems(root)
        const pending = items.filter(
            (item) => !terminalStatuses.includes(readStatus(item))
        )
        const stats = uploadRequestStats()

        sawItem = sawItem || items.length > itemsAtStart

        // The visitor pulled the file back out. Nothing is wrong, so nothing is reported and the
        // rules below — which would otherwise accuse the form of stalling — never get a turn.
        if (sawItem && items.length <= itemsAtStart) {
            recordUploadEvent('attempt:cancelled', 'the item was removed again')
            flushUploadReport('cancelled by the visitor')
            hideMessage(root)
            stopWatchdog(root)

            return
        }

        if (!pending.length && items.length > itemsAtStart) {
            recordUploadEvent(
                'attempt:settled',
                items.map(describeItem).join(' | ')
            )
            flushUploadReport('settled')
            stopWatchdog(root)

            return
        }

        if (elapsed > noRequestAfter) {
            // Nothing in the list and nothing refused by our own check: Uppy dropped the file for
            // a reason it never shows.
            if (items.length <= itemsAtStart) {
                report(
                    'no-item',
                    'no list item was created',
                    messages.stalled,
                    {
                        onRetry: () => retryUpload(root)
                    }
                )
            } else if (stats.requests === 0 && pending.length) {
                // The one the visitor reported: an item that sits there while the network stays
                // quiet, because Uppy never got as far as sending anything.
                report(
                    'no-request',
                    pending.map(describeItem).join(' | '),
                    messages.stalled,
                    { onRetry: () => retryUpload(root) }
                )
            }
        }

        // A request that never reports a single byte falls back to the start of the attempt, so
        // "sent but silent" is caught as well as "stopped halfway".
        const lastProgress = stats.lastProgressAt || startedAt

        const silentFor = Date.now() - lastProgress

        // Deliberately not tied to the item's own phase: a request that stalls before its first
        // byte leaves Formie's label on "Preparing…" for as long as it hangs.
        if (
            stats.requests > 0 &&
            pending.length &&
            silentFor > noProgressAfter
        ) {
            report(
                'no-progress',
                `${Math.round(silentFor / 1000)}s without progress | ${pending.map(describeItem).join(' | ')}`,
                messages.slow,
                { tone: 'wait' }
            )
        }

        // Uppy gives up after two minutes without progress and then retries four times, so left
        // alone this can stare back at the visitor for many minutes. Offer the way out earlier.
        if (stats.requests > 0 && pending.length && silentFor > stalledAfter) {
            report(
                'stalled',
                `${Math.round(silentFor / 1000)}s without progress | ${pending.map(describeItem).join(' | ')}`,
                messages.stalled,
                { onRetry: () => retryUpload(root) }
            )
        }

        // Timed from the moment the phase started, not from the start of the attempt: a large
        // photo on a slow connection can legitimately spend minutes uploading before the server
        // gets its turn.
        const isProcessing = pending.some(
            (item) => readStatus(item) === 'processing'
        )

        processingSince = isProcessing ? (processingSince ?? Date.now()) : null

        if (processingSince && Date.now() - processingSince > processingAfter) {
            report(
                'processing',
                `${Math.round((Date.now() - processingSince) / 1000)}s waiting on the server | ${pending.map(describeItem).join(' | ')}`,
                messages.slow,
                { tone: 'wait' }
            )
        }

        if (elapsed > watchdogTimeout) {
            recordUploadEvent(
                'watchdog:gave-up',
                pending.map(describeItem).join(' | ')
            )
            flushUploadReport('gave up watching')
            stopWatchdog(root)
        }
    }, watchdogInterval)
}

// Formie rebuilds the list item on every state change of the upload, so the observer is both our
// timeline of what the field did and the trigger for the Dutch status text.
const observeList = (root) => {
    const list = root.querySelector(listSelector)

    if (!list || list.dataset.requestUploadObserved === 'true') {
        return
    }

    list.dataset.requestUploadObserved = 'true'

    const seen = new WeakMap()

    const sync = () => {
        getItems(root).forEach((item) => {
            prepareFilename(item)

            const status = readStatus(item)
            const progress = readProgress(item)
            const signature = `${status}:${progress}`

            setStatusText(item)

            if (seen.get(item) === signature) {
                return
            }

            // The percentage moves constantly; only a change of phase is worth a line.
            if ((seen.get(item) ?? '').split(':')[0] !== status) {
                recordUploadEvent(`item:${status}`, describeItem(item))
            }

            seen.set(item, signature)
        })
    }

    new MutationObserver(sync).observe(list, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'data-progress']
    })

    sync()
}

const describeFile = (file) =>
    `${file.name} ${file.size}b ${file.type || 'no type'}`

/**
 * Everything that follows a visitor handing over files, whichever way they did it.
 */
const handleFiles = (root, files, source) => {
    hideMessage(root)
    startUploadAttempt(`${source} | ${files.map(describeFile).join(' | ')}`)

    const refused = files
        .map((file) => ({ file, ...(refusalReason(file, root) ?? {}) }))
        .filter((entry) => entry.reason)

    refused.forEach(({ file, reason, limit }) => {
        recordUploadEvent(`refused:${reason}`, describeFile(file))

        showMessage(
            root,
            reason === 'size' ? messages.size(file, limit) : messages.type
        )
    })

    if (refused.length === files.length) {
        flushUploadReport('every file refused before upload')

        return
    }

    observeList(root)
    startWatchdog(root)
}

// Capture phase on the document, so this runs before Formie's own listener on the input — and
// before Uppy has had the chance to refuse the file without saying so.
const onFileChosen = (event) => {
    const input = event.target

    if (!input?.matches?.(inputSelector) || !input.files?.length) {
        return
    }

    const root = input.closest(rootSelector)

    if (root) {
        handleFiles(root, Array.from(input.files), 'picker')
    }
}

// Cancelling is a click on Formie's own cross. Worth a line of its own: an upload the visitor
// walked away from reads very differently from one that failed.
const onRemoveClicked = (event) => {
    const root = event.target?.closest?.(removeSelector)?.closest(rootSelector)

    if (root?.uploadWatchdog) {
        recordUploadEvent('ui:remove')
    }
}

// Dragging a file onto the dropzone never touches the file input, so it raises no `change` at all:
// Formie reads `dataTransfer` in its own `drop` handler and hands the files straight to Uppy. Left
// alone, the whole drag & drop route would be invisible here — no diagnostics, and a refused file
// as silent as it ever was. Bound where Formie binds it, so a drop that Formie ignores is ignored
// here too.
const onFileDropped = (event) => {
    const root = event.target
        ?.closest?.(dropzoneSelector)
        ?.closest(rootSelector)
    const files = Array.from(event.dataTransfer?.files ?? [])

    if (root && files.length) {
        handleFiles(root, files, 'drop')
    }
}

export const initRequestUpload = (scope = document) => {
    if (isSamsungInternet()) {
        // Samsung Internet's file picker hides photos behind a menu entry nobody finds.
        scope
            .querySelectorAll('[data-request-samsung-hint]')
            .forEach((hint) => {
                hint.hidden = false
            })
    }

    scope.querySelectorAll?.(rootSelector).forEach((root) => {
        observeList(root)
    })
}

document.addEventListener('change', onFileChosen, true)
document.addEventListener('drop', onFileDropped, true)
document.addEventListener('click', onRemoveClicked, true)
